import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { classString } from 'helpers'
import useLazyLoad from 'hooks/useLazyLoad'

const MP4_SUPPORTED_FORMATS = ['mp4', 'mov']
const WEBM_SUPPORTED_FORMATS = []

// TODO: make sure 'aboveTheFold' GIFVideos don't have a black background/border. lazy loaded ones
// remove the background via 'lozad-active' class
const StyledVideo = styled.video.withConfig({
  shouldForwardProp: (prop) => ['noThumbnailSpecified'].indexOf(prop) === -1,
})`
  width: 100%;

  ${({ noThumbnailSpecified }) =>
    noThumbnailSpecified &&
    `
    background: rgba(255,255,255,0.5);
    transition: background 0.2s ease-in;
  `}

  &.lozad-active {
    background: none;
  }
`

function numberifyString (strNumber) {
  if (typeof width === 'string') {
    strNumber = parseInt(strNumber, 10)
    if (isNaN(strNumber)) {
      throw new Error('must be a number; passed string is invalid')
    }
  }

  return strNumber
}

function GIFVideo ({
  src,
  thumbnail,
  aboveTheFold,
  width,
  height,
  fallback,
  className,
  ...others
}) {
  width = width ? numberifyString(width) : undefined
  height = height ? numberifyString(height) : undefined
  const { renderPolyFill, ...otherProps } = useLazyLoad(aboveTheFold)
  const videoEl = useRef(null)

  const fileExt = src?.split('.')?.pop()

  let type = null
  if (MP4_SUPPORTED_FORMATS.includes(fileExt)) {
    type = 'video/mp4'
  } else if (WEBM_SUPPORTED_FORMATS.includes(fileExt)) {
    type = 'video/webm'
  } else {
    throw new Error(
      'File format not recognized. GIFVideo only supports mp4 and webm formats.'
    )
  }

  const handleDblClick = () => {
    const videoElement = videoEl.current
    if (!videoElement) return
    if (videoElement.requestFullscreen) {
      videoElement.requestFullscreen()
    } else if (videoElement.mozRequestFullScreen) {
      // Firefox
      videoElement.mozRequestFullScreen()
    } else if (videoElement.webkitRequestFullscreen) {
      // Chrome, Safari, Opera
      videoElement.webkitRequestFullscreen()
    } else if (videoElement.msRequestFullscreen) {
      // IE/Edge
      videoElement.msRequestFullscreen()
    }
  }

  if (aboveTheFold) {
    return (
      <>
        {renderPolyFill()}
        <StyledVideo
          loop
          autoPlay
          muted
          playsInline
          noThumbnailSpecified={!thumbnail}
          poster={thumbnail}
          data-poster={thumbnail}
          width={width}
          height={height}
          className={className}
          onDoubleClick={handleDblClick}
          ref={videoEl}
          {...otherProps}
          {...others}
        >
          <source src={src} type={type} />
          {fallback}
        </StyledVideo>
      </>
    )
  }

  return (
    <>
      {renderPolyFill()}
      <StyledVideo
        loop
        autoPlay
        muted
        playsInline
        noThumbnailSpecified={!thumbnail}
        poster={thumbnail}
        data-poster={thumbnail}
        width={width}
        height={height}
        data-toggle-class="lozad-active"
        className={classString(['lozad', className])}
        ref={videoEl}
        onDoubleClick={handleDblClick}
        {...otherProps}
        {...others}
      >
        <source src={src} type={type} />
        {fallback}
      </StyledVideo>
    </>
  )
}

GIFVideo.defaultProps = {
  aboveTheFold: false,
}

GIFVideo.propTypes = {
  src: PropTypes.string.isRequired,
  aboveTheFold: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number,
  thumbnail: PropTypes.string,
  fallback: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node)]),
}

export default GIFVideo
